import { Component, EventEmitter, Output, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { FormsModule } from "@angular/forms";
import { ApiService } from "../../api.service";
import { Router } from "@angular/router";
import { CommonService } from "../../shared/common.service";
import { Site } from "../../study.model";

@Component({
  selector: "app-add-location",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: "./add-location.component.html",
  styleUrls: ["./add-location.component.scss"],
})
export class AddLocationComponent {
  @Output() locationAddedEvent = new EventEmitter<void>(); // <-- Event for parent
  @Output() closeFormEvent = new EventEmitter<void>(); // <-- Event for parent
  @Input() existingSites: Site[] = []; // <-- Input to receive existing sites for duplicate checking

  locationForm: FormGroup;
  serverErrorMessage: string = "";
  locationAdded: boolean = false;

  // dropdowns (only city/state/country)
  cityDropdownOpen = false;
  stateDropdownOpen = false;
  countryDropdownOpen = false;

  states: string[] = [];
  filteredStates: string[] = [];
  cities: string[] = [];
  filteredCities: string[] = [];
  countries: string[] = ["India"]; // Add this if not defined elsewhere

  // Fixed: Single consistent data structure
  locationMapping: { [city: string]: string } = {}; // key: city, value: array of cities

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private commonService: CommonService
  ) {
    this.locationForm = this.fb.group({
      // site_id: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9]{6}$/)]],
      site_name: ["", Validators.required],
      site_address: ["", Validators.required],
      city: ["", Validators.required],
      state: ["", Validators.required],
      country: ["India", Validators.required],
    });
  }

  ngOnInit() {
    this.loadLocationData();
  }

  loadLocationData() {
    this.apiService.getLocationMap().subscribe({
      next: (data: any[]) => {
        console.log("API Response:", data); // Debug log

        // Clear existing data
        this.locationMapping = {};

        // Process the API response: [{"city": "Port Blair", "state_name": "Andaman and Nicobar Islands"}, ...]
        data.forEach((item) => {
          this.locationMapping[item.city] = item.state_name;
        });

        // Populate states dropdown
        this.states = [...new Set(Object.values(this.locationMapping))];
        this.filteredStates = [...this.states];

        // Populate states dropdown
        this.cities = Object.keys(this.locationMapping).sort();
        this.filteredCities = [...this.cities];

        console.log("Processed Location Mapping:", this.locationMapping); // Debug log
      },
      error: (err) => {
        console.error("Failed to fetch location mapping:", err);
        alert("Could not load location data. Please try again later.");
      },
    });
  }

  // --- Dropdown togglers ---
  toggleCityDropdown() {
    this.cityDropdownOpen = !this.cityDropdownOpen;
    if (this.cityDropdownOpen) {
      this.stateDropdownOpen = false; // city open ayite state close avvali
      this.countryDropdownOpen = false; // optional: country kuda close cheyachu
    }
  }
  toggleStateDropdown() {
    this.stateDropdownOpen = !this.stateDropdownOpen;
    if (this.stateDropdownOpen) {
      this.cityDropdownOpen = false; // state open ayite city close avvali
      this.countryDropdownOpen = false; // optional
    }
  }
  toggleCountryDropdown() {
    this.countryDropdownOpen = !this.countryDropdownOpen;
    if (this.countryDropdownOpen) {
      this.cityDropdownOpen = false;
      this.stateDropdownOpen = false;
    }
  }

  // --- Selectors ---

  selectCountry(country: string) {
    this.locationForm.controls["country"].setValue(country);

    // Reset state and city when country changes
    this.locationForm.controls["state"].setValue("");
    this.locationForm.controls["city"].setValue("");

    // Reset filtered arrays
    this.filteredStates = [...this.states];
    this.filteredCities = [];

    this.countryDropdownOpen = false;
  }

  selectState(state: string) {
    this.locationForm.controls["state"].setValue(state);

    // Clear city selection when state changes
    this.locationForm.controls["city"].setValue("");

    // Update filtered cities based on selected state
    const citiesForState = Object.keys(this.locationMapping).filter(
      (city) => this.locationMapping[city] === state
    );
    this.filteredCities = [...citiesForState];

    this.stateDropdownOpen = false;
  }

  selectCity(city: string) {
    this.locationForm.controls["city"].setValue(city);

    // Automatically set state based on selected city
    const correspondingState = this.locationMapping[city];
    if (correspondingState) {
      this.locationForm.controls["state"].setValue(correspondingState);
      this.locationForm.controls["country"].setValue("India");

      this.locationForm.controls["state"].disable();
      this.locationForm.controls["country"].disable();
    }

    this.cityDropdownOpen = false;
  }
  onCityChange(event: any) {
    const city = event.target.value;
    
    if (!city || city === '') {
      // ⭐ Re-enable fields when city is cleared
      this.locationForm.controls["state"].enable();
      this.locationForm.controls["country"].enable();
      this.locationForm.controls["state"].setValue("");
      this.locationForm.controls["country"].setValue("");
    }
  }

  filterStates(event: any) {
    const query = event.target.value.toLowerCase().trim();
    this.filteredStates = this.states.filter((state) =>
      state.toLowerCase().includes(query)
    );
  }

  filterCities(event: any) {
    const query = event.target.value.toLowerCase().trim();
    const selectedState = this.locationForm.controls["state"].value;

    // Only filter cities if a state is selected
    if (selectedState) {
      const citiesForState = Object.keys(this.locationMapping).filter(
        (city) => this.locationMapping[city] === selectedState
      );
      this.filteredCities = citiesForState.filter((city) =>
        city.toLowerCase().includes(query)
      );
    } else {
      // If no state selected, show all cities that match the query
      const allCities: string[] = [];
      Object.values(this.locationMapping).forEach((cities) => {
        allCities.push(...cities);
      });
      this.filteredCities = [...new Set(allCities)]
        .filter((city) => city.toLowerCase().includes(query))
        .sort();
    }
  }

  // --- Duplicate checking method ---
  checkForDuplicates(siteName: string, siteAddress: string): boolean {
    return this.existingSites.some(
      (site) =>
        site.site_name?.toLowerCase().trim() ===
          siteName.toLowerCase().trim() &&
        site.site_address?.toLowerCase().trim() ===
          siteAddress.toLowerCase().trim()
    );
  }

  onSubmit() {
    if (this.locationForm.invalid) {
      alert("Please fill all required fields correctly!");
      return;
    }

    // Check for duplicates
    const siteName = this.locationForm.value.site_name;
    const siteAddress = this.locationForm.value.site_address;

    if (this.checkForDuplicates(siteName, siteAddress)) {
      alert(
        "A site with the same name and address already exists. Please use a different name or address."
      );
      return;
    }

    const created_by = this.commonService.getUserData().id;
    
    // ⭐ Use getRawValue() to include disabled fields
    const formValues = this.locationForm.getRawValue();
    
    const payload = {
      site_name: formValues.site_name,
      site_address: formValues.site_address,
      city: formValues.city,
      state: formValues.state,
      country: formValues.country,
      created_by: created_by,
    };

    this.apiService.addSite(payload).subscribe({
      next: () => {
        this.locationForm.reset({ country: "India" });
        
        // ⭐ Re-enable fields on reset
        this.locationForm.controls["state"].enable();
        this.locationForm.controls["country"].enable();
        
        this.filteredStates = [...this.states];
        this.filteredCities = [];

        alert("Site added successfully!");
        this.locationAddedEvent.emit();
        this.closeFormEvent.emit();
      },
      error: (err) => {
        console.error("Error adding site:", err);
        console.error("Error status:", err?.status);
        console.error("Error error object:", err?.error);
        console.error("Full error structure:", JSON.stringify(err, null, 2));
        
        // Extract error message from response
        let errorMessage = "Something went wrong while adding the site.";
        
        if (err?.error) {
          // Check for message property (your response format: { success: false, message: "..." })
          if (err.error.message && typeof err.error.message === 'string') {
            errorMessage = err.error.message;
          } 
          // Check for error property
          else if (err.error.error && typeof err.error.error === 'string') {
            errorMessage = err.error.error;
          } 
          // Check if error itself is a string
          else if (typeof err.error === 'string') {
            errorMessage = err.error;
          }
        }
        // Check if message is directly on error object
        else if (err?.message && typeof err.message === 'string') {
          errorMessage = err.message;
        }
        
        console.error("Extracted error message:", errorMessage);
        
        // Show alert with the error message from response
        alert(errorMessage);
      },
    });
  }
  
  onCancel() {
    this.locationForm.reset({ country: "India" });
    
    // ⭐ Re-enable fields on cancel
    this.locationForm.controls["state"].enable();
    this.locationForm.controls["country"].enable();
    
    this.serverErrorMessage = "";
    this.filteredStates = [...this.states];
    this.filteredCities = [];
    this.locationAdded = false;
  }
}
